import { FC, memo, useCallback, useMemo, useState } from 'react';
import { FaLock, FaUnlock } from 'react-icons/fa6';
import { Dropdown, MenuProps, message } from 'antd';
import { DeleteIcon, AddToQueueIcon, EditIcon, AddedToLibrary, AddToLibrary } from '../Icons';

// Services
import { playerService } from '../../services/player';
import { playlistService } from '../../services/playlists';

// Utils
import { useTranslation } from 'react-i18next';

// Interface
import type { Playlist } from '../../interfaces/playlists';

// Redux
import { uiActions } from '../../store/slices/ui';
import { fetchQueue } from '../../store/slices/queue';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { yourLibraryActions } from '../../store/slices/yourLibrary';
import { editPlaylistModalActions } from '../../store/slices/editPlaylistModal';
import { CascadeMenu } from './CascadeMenu';
import { useCascadeSelections } from '../../hooks/useCascadeSelections';

interface PlayistActionsWrapperProps {
  playlist: Playlist;
  isCurrent?: boolean;
  onRefresh?: () => void;
  trigger?: ('contextMenu' | 'click')[];
  children: React.ReactNode | React.ReactNode[];
}

export const PlayistActionsWrapper: FC<PlayistActionsWrapperProps> = memo((props) => {
  const { children, playlist } = props;

  const { t } = useTranslation(['playlist']);

  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.auth.user?.id);
  const myPlaylists = useAppSelector((state) => state.yourLibrary.myPlaylists);
  const canEdit = useMemo(() => userId === playlist.owner?.id, [userId, playlist.owner?.id]);

  const handleUserValidation = useCallback(
    (button?: boolean) => {
      if (!userId) {
        dispatch(button ? uiActions.openLoginButton() : uiActions.openLoginTooltip());
        return false;
      }
      return true;
    },
    [dispatch, userId]
  );

  const inLibrary = useMemo(() => {
    return myPlaylists.some((p) => p.id === playlist.id);
  }, [myPlaylists, playlist.id]);

  const [isApplyingCascade, setIsApplyingCascade] = useState(false);

  const {
    scopes: cascadeScopes,
    selections: cascadeSelections,
    setSelection: setCascadeSelection,
    resetSelections: resetCascadeSelections,
  } = useCascadeSelections({
    entityType: 'playlist',
    mode: inLibrary ? 'remove' : 'save',
  });

  const ownerPlaylistOverride = useMemo(() => ({ playlist: false as const }), []);

  // Owner-only cascades (no playlist toggle; separate save/remove instances to keep defaults clean)
  const {
    scopes: ownerSaveScopes,
    selections: ownerSaveSelections,
    setSelection: setOwnerSaveSelection,
    resetSelections: resetOwnerSaveSelections,
  } = useCascadeSelections({
    entityType: 'playlist',
    mode: 'save',
    overrides: ownerPlaylistOverride,
  });

  const {
    scopes: ownerRemoveScopes,
    selections: ownerRemoveSelections,
    setSelection: setOwnerRemoveSelection,
    resetSelections: resetOwnerRemoveSelections,
  } = useCascadeSelections({
    entityType: 'playlist',
    mode: 'remove',
    overrides: ownerPlaylistOverride,
  });

  const ownerScopes = useMemo(
    () => ownerSaveScopes.filter((scope) => scope !== 'playlist'),
    [ownerSaveScopes]
  );

  const handleCascadeToggle = useCallback(
    (scope: (typeof cascadeScopes)[number], next: boolean) => {
      setCascadeSelection(scope, next);
    },
    [setCascadeSelection]
  );

  const handleCascadeApply = useCallback(async () => {
    if (!handleUserValidation(true)) return;
    setIsApplyingCascade(true);

    const cascades = {
      playlist: cascadeSelections.playlist ?? true,
      recordings: cascadeSelections.recordings ?? false,
      tracks: cascadeSelections.tracks ?? false,
    };

    try {
      if (inLibrary) {
        await dispatch(
          yourLibraryActions.removePlaylistWithCascade({
            playlistId: playlist.id,
            playlistName: playlist.name,
            cascades,
          })
        ).unwrap();
      } else {
        await dispatch(
          yourLibraryActions.savePlaylistWithCascade({
            playlistId: playlist.id,
            playlistName: playlist.name,
            cascades,
          })
        ).unwrap();
      }
      resetCascadeSelections();
    } catch (error) {
      const errorMessage =
        typeof error === 'string' && error.trim().length > 0
          ? error
          : t('Something went wrong');
      message.error(errorMessage);
    } finally {
      setIsApplyingCascade(false);
    }
  }, [
    cascadeSelections.playlist,
    cascadeSelections.recordings,
    cascadeSelections.tracks,
    dispatch,
    handleUserValidation,
    inLibrary,
    playlist.id,
    playlist.name,
    resetCascadeSelections,
    t,
  ]);

  const cascadeLabels = useMemo(() => {
    const mode = inLibrary ? 'remove' : 'save';
    if (mode === 'save') {
      return {
        playlist: t('Save playlist'),
        recordings: t('Save recordings'),
        tracks: t('Save tracks'),
      };
    }
    return {
      playlist: t('Remove playlist'),
      recordings: t('Remove recordings'),
      tracks: t('Remove tracks'),
    };
  }, [inLibrary, t]);

  const ownerCascadeLabels = useMemo(
    () => ({
      recordings: t('Save recordings'),
      tracks: t('Save tracks'),
    }),
    [t]
  );

  const ownerRemoveCascadeLabels = useMemo(
    () => ({
      recordings: t('Remove recordings'),
      tracks: t('Remove tracks'),
    }),
    [t]
  );

  const handleOwnerCascadeApply = useCallback(
    async (mode: 'save' | 'remove') => {
      if (!handleUserValidation(true)) return;
      setIsApplyingCascade(true);
      const selections =
        mode === 'save' ? ownerSaveSelections : ownerRemoveSelections;
      const cascades = {
        playlist: false,
        recordings: selections.recordings ?? false,
        tracks: selections.tracks ?? false,
      };

      try {
        if (mode === 'save') {
          await dispatch(
            yourLibraryActions.savePlaylistWithCascade({
              playlistId: playlist.id,
              playlistName: playlist.name,
              cascades,
            })
          ).unwrap();
          resetOwnerSaveSelections();
        } else {
          await dispatch(
            yourLibraryActions.removePlaylistWithCascade({
              playlistId: playlist.id,
              playlistName: playlist.name,
              cascades,
            })
          ).unwrap();
          resetOwnerRemoveSelections();
        }
      } catch (error) {
        const errorMessage =
          typeof error === 'string' && error.trim().length > 0
            ? error
            : t('Something went wrong');
        message.error(errorMessage);
      } finally {
        setIsApplyingCascade(false);
      }
    },
    [
      dispatch,
      handleUserValidation,
      ownerRemoveSelections,
      ownerSaveSelections,
      playlist.id,
      playlist.name,
      resetOwnerRemoveSelections,
      resetOwnerSaveSelections,
      t,
    ]
  );

  const items = useMemo(() => {
    const items: MenuProps['items'] = [];

    const pushRecordingTrackCascades = () => {
      items.push({
        key: 'owner-cascade-save',
        label: t('Save playlist recordings/tracks'),
        icon: <AddToLibrary style={{ height: 16, width: 16, marginInlineEnd: 0 }} />,
        className: 'cascade-menu__trigger-item',
        popupClassName: 'cascade-menu__popup',
        children: [
          {
            key: 'owner-cascade-save-options',
            label: (
              <CascadeMenu
                entityType='playlist'
                mode='save'
                scopes={ownerScopes as any}
                selections={ownerSaveSelections as any}
                labels={ownerCascadeLabels as any}
                onToggle={setOwnerSaveSelection as any}
                applyLabel={t('Apply')}
                onApply={() => handleOwnerCascadeApply('save')}
                onCancel={() => resetOwnerSaveSelections()}
                applyDisabled={isApplyingCascade}
              />
            ),
            style: { padding: 0, height: 'auto' },
          },
        ],
      });

      items.push({
        key: 'owner-cascade-remove',
        label: t('Remove playlist recordings/tracks'),
        icon: <AddedToLibrary style={{ height: 16, width: 16, marginInlineEnd: 0 }} />,
        className: 'cascade-menu__trigger-item',
        popupClassName: 'cascade-menu__popup',
        children: [
          {
            key: 'owner-cascade-remove-options',
            label: (
              <CascadeMenu
                entityType='playlist'
                mode='remove'
                scopes={ownerScopes as any}
                selections={ownerRemoveSelections as any}
                labels={ownerRemoveCascadeLabels as any}
                onToggle={setOwnerRemoveSelection as any}
                applyLabel={t('Apply')}
                onApply={() => handleOwnerCascadeApply('remove')}
                onCancel={() => resetOwnerRemoveSelections()}
                applyDisabled={isApplyingCascade}
              />
            ),
            style: { padding: 0, height: 'auto' },
          },
        ],
      });
    };

    if (canEdit) {
      items.push(
        {
          label: t('Edit details'),
          key: 1,
          icon: <EditIcon />,
          onClick: () => {
            if (!handleUserValidation()) return;
            dispatch(editPlaylistModalActions.setPlaylist({ playlist }));
          },
        },
        {
          label: t('Delete playlist'),
          key: '2',
          disabled: true,
          icon: <DeleteIcon />,
        },
        {
          type: 'divider',
        }
      );

      if (playlist.public) {
        items.push({
          label: t('Make private'),
          key: 4,
          icon: <FaLock size={16} fill='#bababa' style={{ padding: 0, marginInlineEnd: 0 }} />,
          onClick: () => {
            if (!handleUserValidation()) return;
            return playlistService
              .changePlaylistDetails(playlist.id, { public: false })
              .then(() => {
                props.onRefresh?.();
                message.open({
                  type: 'success',
                  content: t('Playlist is now private'),
                });
              });
          },
        });
      } else {
        items.push({
          label: t('Make public'),
          key: 5,
          icon: <FaUnlock size={16} fill='#bababa' style={{ padding: 0, marginInlineEnd: 0 }} />,
          onClick: () => {
            if (!handleUserValidation()) return;
            return playlistService
              .changePlaylistDetails(playlist.id, { public: true, collaborative: false })
              .then(() => {
                props.onRefresh?.();
                message.open({
                  type: 'success',
                  content: t('Playlist is now public'),
                });
              });
          },
        });
      }
    } else {
      items.push({
        label: t(inLibrary ? 'Remove from Your Library' : 'Add to Your Library'),
        key: 'cascade-playlist',
        icon: inLibrary ? (
          <AddedToLibrary style={{ height: 16, width: 16, marginInlineEnd: 0 }} />
        ) : (
          <AddToLibrary style={{ height: 16, width: 16, marginInlineEnd: 0 }} />
        ),
        className: 'cascade-menu__trigger-item',
        popupClassName: 'cascade-menu__popup',
        children: [
          {
            key: 'cascade-playlist-options',
            label: (
              <CascadeMenu
                entityType='playlist'
                mode={inLibrary ? 'remove' : 'save'}
                scopes={cascadeScopes}
                selections={cascadeSelections as any}
                labels={cascadeLabels as any}
                onToggle={handleCascadeToggle as any}
                applyLabel={t('Apply')}
                onApply={handleCascadeApply}
                onCancel={() => {
                  resetCascadeSelections();
                }}
                applyDisabled={isApplyingCascade}
              />
            ),
            style: { padding: 0, height: 'auto' },
          },
        ],
      });
      items.push({ type: 'divider' });
    }

    pushRecordingTrackCascades();

    items.push({
      label: t('Add to queue'),
      key: '3',
      disabled: true,
      icon: <AddToQueueIcon />,
      onClick: () => {
        if (!handleUserValidation()) return;
        return playerService.addToQueue(playlist.uri).then(() => {
          dispatch(fetchQueue());
          message.open({
            type: 'success',
            content: t('Added to queue'),
          });
        });
      },
    });

    return items;
  }, [
    canEdit,
    cascadeLabels,
    cascadeScopes,
    cascadeSelections,
    handleCascadeApply,
    handleCascadeToggle,
    handleUserValidation,
    inLibrary,
    isApplyingCascade,
    ownerCascadeLabels,
    ownerRemoveCascadeLabels,
    ownerRemoveSelections,
    ownerSaveSelections,
    ownerScopes,
    playlist,
    props,
    resetCascadeSelections,
    resetOwnerRemoveSelections,
    resetOwnerSaveSelections,
    handleOwnerCascadeApply,
    t,
    dispatch,
  ]);

  return (
    <Dropdown menu={{ items, triggerSubMenuAction: 'hover' }} trigger={props.trigger}>
      {children}
    </Dropdown>
  );
});
